import { isSystemInputFeature } from '@/tools/models/template.types';
import type { TFunction } from 'i18next';
import type { TemplateInput } from '@/tools/models/template.types';
import type { TemplateDependencyData, TemplateRuleStatus } from '@/tools/models/templateDependencies.queries';
import type { InstanceOutputMappingV1 } from '@seeqdev/api/model/InstanceOutputMappingV1';
import type { BadgeVariant } from '@seeqdev/qomponents';

/** A single badge: which visual variant, its already-translated text, and the tooltip naming what it counts. */
export type OutputBadge = {
  variant: BadgeVariant;
  text: string;
  /** Already-translated tag rendered ahead of the text, e.g. "Formula 1". Only on per-formula badges. */
  label?: string;
  /** Already-translated hover text naming the inputs this badge reports on. */
  tooltip?: string;
};

/**
 * One output's badges, grouped the way they are meant to be read.
 *
 * The grouping is the producer's answer, not something the renderer works out: an output computed by a single
 * formula has a single group, and an output with alternative formulas has one group per formula, because
 * satisfying any one formula's inputs produces the output and a single combined count would misreport that.
 */
export type OutputBadges = {
  /**
   * Already-translated line explaining how to read the groups below. Present only when there is more than one
   * group, since that is the only case that needs explaining.
   */
  helperText?: string;
  isComplete?: boolean;
  /** One rendered line each. Empty when the output has no inputs at all, so it gets no badges. */
  groups: OutputBadge[][];
};

/**
 * An output is produced as soon as any one of its rules is evaluable -- alternative formulas let that happen
 * while the inputs of the other branches are still unmapped. The appserver decides each rule's `isComplete`;
 * see appserver/items/src/main/kotlin/com/seeq/appserver/models/TEMPLATE_DEPENDENCIES_SPEC.md.
 *
 * An output with no rules reported has nothing that can produce it, so it is not complete.
 */
export function isOutputComplete(ruleStatuses: TemplateRuleStatus[] | undefined): boolean {
  return !!ruleStatuses?.some((ruleStatus) => ruleStatus.isComplete);
}

/**
 * The inputs an *output* cannot be produced without, as opposed to the ones one of its rules cannot be
 * evaluated without: an input only some of its rules name is one the remaining rules can produce it without.
 *
 * This is the question the apply request asks. An input outside this set can always be left out of the
 * request while unmapped, because some rule can still produce the output without it.
 *
 * Kept distinct from `TemplateRuleStatus.requiredInputCanonicalIds` deliberately -- the endpoint answers the
 * per-rule question, and this is the only place that folds those answers into a per-output one.
 */
function requiredByEveryRuleOfOutput(ruleStatuses: TemplateRuleStatus[]): Set<string> {
  const [firstRule, ...otherRules] = ruleStatuses;
  if (!firstRule) return new Set();
  return new Set(
    firstRule.requiredInputCanonicalIds.filter((canonicalId) =>
      otherRules.every((ruleStatus) => ruleStatus.requiredInputCanonicalIds.includes(canonicalId)),
    ),
  );
}

/**
 * The inputs at least one of the output's rules cannot be evaluated without.
 *
 * This is the question the form's "(Optional)" label asks, and it is deliberately the broader of the two.
 * Telling someone an input is optional means they never have to fill it in, which is not true of an input
 * that a fallback formula names: picking that formula's route obliges them to map it. Only an input no rule
 * requires at all -- a variable parameter, or a member of a `parameters()` group -- is optional in that sense.
 */
function requiredByAnyRuleOfOutput(ruleStatuses: TemplateRuleStatus[]): Set<string> {
  return new Set(ruleStatuses.flatMap((ruleStatus) => ruleStatus.requiredInputCanonicalIds));
}

/** Every input any of the output's rules touches, required or optional. */
function allInputCanonicalIdsForOutput(ruleStatuses: TemplateRuleStatus[]): Set<string> {
  return new Set(
    ruleStatuses.flatMap((ruleStatus) => [
      ...ruleStatus.requiredInputCanonicalIds,
      ...ruleStatus.optionalInputCanonicalIds,
    ]),
  );
}

/**
 * Builds each selected output's badges, each one already carrying the tooltip that says what it counts.
 *
 * Deciding the tooltip here rather than downstream is the point: only this function knows whether a given
 * badge reports on required inputs or optional ones. Nothing after this has to infer that from a badge's
 * styling.
 *
 * @param outputRuleStatuses Per-output rule statuses from the appserver, keyed by lowercase output canonical ID.
 * @param mappedInputCanonicalIds Lowercase canonical IDs of the inputs mapped so far.
 * @param inputNameByCanonicalId Display names for the tooltips, keyed by lowercase input canonical ID.
 */
export function computeOutputBadges(
  outputRuleStatuses: Map<string, TemplateRuleStatus[]>,
  mappedInputCanonicalIds: Set<string>,
  inputNameByCanonicalId: Map<string, string>,
  t: TFunction,
): Map<string, OutputBadges> {
  const unmapped = (canonicalIds: string[]) => canonicalIds.filter((id) => !mappedInputCanonicalIds.has(id));

  const counts = (canonicalIds: string[]) => ({
    mapped: canonicalIds.filter((id) => mappedInputCanonicalIds.has(id)).length,
    total: canonicalIds.length,
  });

  const tooltip = (key: string, canonicalIds: string[]): string | undefined => {
    const names = canonicalIds
      .map((canonicalId) => inputNameByCanonicalId.get(canonicalId))
      .filter((name): name is string => !!name);
    return names.length > 0 ? t(key, { inputNames: names.join(', ') }) : undefined;
  };

  /** The neutral count of what a rule can still take, and the names behind that count. */
  const optionalBadge = (optionalCanonicalIds: string[]): OutputBadge | undefined =>
    optionalCanonicalIds.length === 0
      ? undefined
      : {
          variant: 'neutral',
          text: t('TEMPLATES.OUTPUT_STATUS_OPTIONAL', counts(optionalCanonicalIds)),
          // Always every optional input, mapped or not: an optional input never blocks submission, so there
          // is nothing "missing" to call out -- the tooltip exists to say what the count refers to.
          tooltip: tooltip('TEMPLATES.OUTPUT_OPTIONAL_INPUTS_TOOLTIP', optionalCanonicalIds),
        };

  /**
   * Why a rule is not evaluable yet: the inputs it is still missing, or -- once those are all mapped -- the
   * optional parameters any one of which would finish it.
   */
  const blockedBadge = (ruleStatus: TemplateRuleStatus, label?: string): OutputBadge => {
    const missingCanonicalIds = unmapped(ruleStatus.requiredInputCanonicalIds);
    return missingCanonicalIds.length > 0
      ? {
          variant: 'error',
          ...(label === undefined ? {} : { label }),
          text: t('TEMPLATES.OUTPUT_STATUS_REQUIRED', counts(ruleStatus.requiredInputCanonicalIds)),
          tooltip: tooltip('TEMPLATES.OUTPUT_MISSING_INPUTS_TOOLTIP', missingCanonicalIds),
        }
      : {
          // Everything the rule strictly requires is mapped, so what is left is a `parameters()` group with no
          // mapped member. A count would read "2 of 2" and look complete.
          variant: 'error',
          ...(label === undefined ? {} : { label }),
          text: t('TEMPLATES.OUTPUT_STATUS_INPUT_NEEDED'),
          tooltip: tooltip(
            'TEMPLATES.OUTPUT_ALTERNATIVE_INPUTS_TOOLTIP',
            unmapped(ruleStatus.optionalInputCanonicalIds),
          ),
        };
  };

  /** One rendered line: the rule's own status, and the optional parameters that belong to it beside it. */
  const ruleGroup = (ruleStatus: TemplateRuleStatus, label?: string): OutputBadge[] => {
    const statusBadge: OutputBadge = ruleStatus.isComplete
      ? { variant: 'success', ...(label === undefined ? {} : { label }), text: t('TEMPLATES.OUTPUT_STATUS_COMPLETE') }
      : blockedBadge(ruleStatus, label);
    // A rule's optional parameters keep their own count on the same line; sitting beside that rule's status
    // badge is what ties them to it.
    return [statusBadge, optionalBadge(ruleStatus.optionalInputCanonicalIds)].filter(
      (badge): badge is OutputBadge => !!badge,
    );
  };

  return new Map(
    Array.from(outputRuleStatuses, ([outputCanonicalId, ruleStatuses]): [string, OutputBadges] => {
      // An output that depends on nothing has no progress to report.
      if (allInputCanonicalIdsForOutput(ruleStatuses).size === 0) {
        return [outputCanonicalId, { groups: [] }];
      }
      if (ruleStatuses.length === 1) {
        return [outputCanonicalId, { groups: [ruleGroup(ruleStatuses[0])] }];
      }
      const isComplete = isOutputComplete(ruleStatuses);
      return [
        outputCanonicalId,
        {
          // Once one formula is complete the output is produced, so the line stops asking for one and says so
          // -- the other formulas' badges are then alternatives the user may still fill in, not blockers.
          helperText: isComplete
            ? t('TEMPLATES.OUTPUT_FORMULA_COMPLETE_HELPER_TEXT')
            : t('TEMPLATES.OUTPUT_FORMULA_REQUIRED_HELPER_TEXT'),
          isComplete,
          groups: ruleStatuses.map((ruleStatus, index) =>
            ruleGroup(ruleStatus, t('FORMULA.FORMULA_COUNT', { count: index + 1 })),
          ),
        },
      ];
    }),
  );
}

/**
 * Shared body of the two classifications below. They differ only in what each selected output counts as
 * required, which `requiredForOutput` decides; everything an output's rules touch but do not require is a
 * candidate, and being required by any one selected output disqualifies an input whatever the others make
 * of it.
 */
function computeUnrequiredInputFeatureIds(
  outputRuleStatuses: Map<string, TemplateRuleStatus[]>,
  selectedOutputCanonicalIds: Set<string>,
  inputFeatures: TemplateInput[],
  requiredForOutput: (ruleStatuses: TemplateRuleStatus[]) => Set<string>,
): Set<string> {
  const candidateCanonicalIds = new Set<string>();
  const requiredCanonicalIds = new Set<string>();
  selectedOutputCanonicalIds.forEach((outputCanonicalId) => {
    const ruleStatuses = outputRuleStatuses.get(outputCanonicalId) ?? [];
    const required = requiredForOutput(ruleStatuses);
    required.forEach((id) => requiredCanonicalIds.add(id));
    allInputCanonicalIdsForOutput(ruleStatuses).forEach((id) => {
      if (!required.has(id)) candidateCanonicalIds.add(id);
    });
  });

  return new Set(
    inputFeatures
      .filter((input) => {
        const canonicalId = input.canonicalId?.toLowerCase();
        return (
          !!input.id &&
          !!canonicalId &&
          candidateCanonicalIds.has(canonicalId) &&
          !requiredCanonicalIds.has(canonicalId)
        );
      })
      .map((input) => input.id!),
  );
}

/**
 * Input feature IDs to label "(Optional)" in the form: the ones no rule of any selected output requires.
 *
 * Narrower than {@link computeOmittableInputFeatureIds} on purpose. An input a fallback formula requires is
 * not optional to the person filling the form in -- choosing that formula's route obliges them to map it --
 * even though the request may still leave it out while another formula can produce the output instead.
 */
export function computeOptionalInputFeatureIds(
  outputRuleStatuses: Map<string, TemplateRuleStatus[]>,
  selectedOutputCanonicalIds: Set<string>,
  inputFeatures: TemplateInput[],
): Set<string> {
  return computeUnrequiredInputFeatureIds(
    outputRuleStatuses,
    selectedOutputCanonicalIds,
    inputFeatures,
    requiredByAnyRuleOfOutput,
  );
}

/**
 * Input feature IDs the apply request may leave out while unmapped: variable parameters, and inputs serving
 * only one formula that another formula can stand in for.
 *
 * Broader than {@link computeOptionalInputFeatureIds}: sending an unmapped mapping for an input the applied
 * outputs can be produced without is what the backend cannot handle, so anything a rule can route around
 * belongs here even when the form calls it required.
 */
export function computeOmittableInputFeatureIds(
  outputRuleStatuses: Map<string, TemplateRuleStatus[]>,
  selectedOutputCanonicalIds: Set<string>,
  inputFeatures: TemplateInput[],
): Set<string> {
  return computeUnrequiredInputFeatureIds(
    outputRuleStatuses,
    selectedOutputCanonicalIds,
    inputFeatures,
    requiredByEveryRuleOfOutput,
  );
}

/**
 * Returns the feature IDs of the inputs required by the selected outputs, or by all
 * outputs if no selection is provided.
 *
 * `dependencyData`'s canonical IDs are already lowercase (see `TemplateDependencyData`), as are
 * `selectedOutputCanonicalIds`; only the input features' own canonical IDs need normalizing.
 */
export function computeVisibleInputFeatureIds(
  dependencyData: TemplateDependencyData,
  selectedOutputCanonicalIds: Set<string> | undefined,
  inputFeatures: TemplateInput[],
): Set<string> {
  const idByCanonicalId = new Map(
    inputFeatures
      .filter((input) => input.id && input.canonicalId)
      .map((input) => [input.canonicalId!.toLowerCase(), input.id!]),
  );

  const visibleCanonicalIds = new Set<string>();
  dependencyData.outputInputDependencies.forEach((inputCanonicalIds, outputCanonicalId) => {
    if (selectedOutputCanonicalIds && !selectedOutputCanonicalIds.has(outputCanonicalId)) return;
    inputCanonicalIds.forEach((id) => visibleCanonicalIds.add(id));
  });

  const visibleFeatureIds = new Set<string>();
  visibleCanonicalIds.forEach((canonicalId) => {
    const featureId = idByCanonicalId.get(canonicalId);
    if (featureId) visibleFeatureIds.add(featureId);
  });
  return visibleFeatureIds;
}

/**
 * Filters input features to those required by the selected outputs, always including the backend-managed
 * ones, which no output declares a dependency on. Returns all inputs until dependency data is available.
 */
export function filterInputFeaturesByOutputs(
  dependencyData: TemplateDependencyData | undefined,
  selectedOutputCanonicalIds: Set<string>,
  inputFeatures: TemplateInput[],
): TemplateInput[] {
  if (!dependencyData || dependencyData.outputInputDependencies.size === 0) return inputFeatures;

  const visibleFeatureIds = computeVisibleInputFeatureIds(dependencyData, selectedOutputCanonicalIds, inputFeatures);
  return inputFeatures.filter(
    (input) => isSystemInputFeature(input.name) || (input.id && visibleFeatureIds.has(input.id)),
  );
}

/**
 * Returns the selected outputs that depend on `outputCanonicalId`. An empty array means
 * the output can be safely unselected.
 *
 * Every canonical ID here is already lowercase: `outputToOutputDependencies` by `TemplateDependencyData`'s
 * invariant, and both `outputCanonicalId` and `selectedOutputCanonicalIds` by their callers.
 */
export function validateOutputUnselection(
  outputCanonicalId: string,
  selectedOutputCanonicalIds: Set<string>,
  outputToOutputDependencies: Map<string, Set<string>>,
): string[] {
  const blockingOutputCanonicalIds: string[] = [];
  for (const otherOutputCanonicalId of selectedOutputCanonicalIds) {
    if (otherOutputCanonicalId === outputCanonicalId) continue;
    if (outputToOutputDependencies.get(otherOutputCanonicalId)?.has(outputCanonicalId)) {
      blockingOutputCanonicalIds.push(otherOutputCanonicalId);
    }
  }
  return blockingOutputCanonicalIds;
}

/**
 * Canonical IDs of the outputs that `outputCanonicalId` depends on. These must be
 * selected too, since `outputCanonicalId` can't be computed without them. `outputToOutputDependencies`
 * already stores each output's complete transitive dependency set, so this is a direct lookup.
 */
export function computeRequiredDependencyOutputCanonicalIds(
  outputCanonicalId: string,
  outputToOutputDependencies: Map<string, Set<string>>,
): Set<string> {
  return outputToOutputDependencies.get(outputCanonicalId) ?? new Set();
}

/** Returns the canonical IDs of outputs that are currently active for an instance. */
export function computePreviouslySelectedOutputCanonicalIds(
  instanceOutputs: InstanceOutputMappingV1[],
  baseDerivedFeatures: Array<{ id?: string; canonicalId?: string }>,
): Set<string> {
  const canonicalIdByFeatureId = new Map(
    baseDerivedFeatures
      .filter((feature) => feature.id && feature.canonicalId)
      .map((feature) => [feature.id!, feature.canonicalId!.toLowerCase()]),
  );

  const previouslySelectedOutputCanonicalIds = new Set<string>();
  instanceOutputs.forEach((output) => {
    if (!output.outputId || output.outputIdInactive || !output.featureId) return;
    const canonicalId = canonicalIdByFeatureId.get(output.featureId);
    if (canonicalId) previouslySelectedOutputCanonicalIds.add(canonicalId);
  });
  return previouslySelectedOutputCanonicalIds;
}

/** Returns the item IDs of outputs that were removed by a reapply. */
export function computeRetiredOutputItemIds(
  previousOutputs: InstanceOutputMappingV1[],
  newOutputs: InstanceOutputMappingV1[],
): string[] {
  const newOutputIds = new Set(newOutputs.map((output) => output.outputId).filter(Boolean));
  return previousOutputs.map((output) => output.outputId).filter((id): id is string => !!id && !newOutputIds.has(id));
}
